import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';
import { Typography } from 'antd';
import { 
  useVoteForScanRequestMutation, 
  useUnVoteForScanRequestMutation, 
  usePolls 
} from '../features/vehicleModels/vehicleModelApi.js';
import { addToast } from '../features/toast/toastsSlice.js';

const { Text } = Typography;

/**
 * Custom hook to manage scan request state and voting logic
 * @param {Object} options - Configuration options
 * @param {Function} options.i18n - i18next instance for translations
 * @param {Function} options.t - Translation function
 * @returns {Object} Scan request state and methods
 */
export const useScanRequestState = ({ i18n, t }) => {
  const dispatch = useDispatch();
  
  // API hooks
  const { polls, history: userVoteHistory, isFetching: isFetchingPoll } = usePolls();
  const [voteForScanRequest] = useVoteForScanRequestMutation();
  const [unVoteForScanRequest] = useUnVoteForScanRequestMutation();
  
  // State management
  const [votes, setVotes] = useState(new Set());
  const [unvotablePolls, setUnvotablePolls] = useState(new Map());

  // Effect to update votes from user vote history
  useEffect(() => {
    if (!isFetchingPoll) {
      const latestVotes = new Set();
      userVoteHistory.forEach(obj => {
        const key = obj['vehicleModelId'];
        latestVotes.add(key);
      });
      setVotes(latestVotes);
    }
  }, [isFetchingPoll, userVoteHistory]);

  // Effect to update unvotable polls (closed/decided polls)
  useEffect(() => {
    if (!isFetchingPoll) {
      const latestPolls = new Map();
      polls.filter(poll => poll.status !== 'IN_REVIEW').forEach(poll => {
        latestPolls.set(poll.vehicleModel.id, poll);
      });
      setUnvotablePolls(latestPolls);
    }
  }, [isFetchingPoll, polls]);

  /**
   * Get scan request state for a specific record
   * @param {Object} record - Vehicle model record
   * @returns {Object} State information for the record
   */
  const getScanRequestState = (record) => {
    if (!record) return null;

    const hasVoted = votes.has(record.id);
    const unvotablePoll = unvotablePolls.get(record.id);
    const isUnvotable = !!unvotablePoll;
    const isEligibleForScanRequest = record?.source === 'internal' && !record?.isDummyInternal;

    return {
      hasVoted,
      isUnvotable,
      unvotablePoll,
      isEligibleForScanRequest,
      canVote: isEligibleForScanRequest && !isUnvotable,
      canUnvote: isEligibleForScanRequest && hasVoted && !isUnvotable
    };
  };

  /**
   * Vote for a scan request
   * @param {number} recordId - Vehicle model ID
   */
  const handleVote = async (recordId) => {
    try {
      await voteForScanRequest({ id: recordId });
      setVotes(prev => new Set([...prev, recordId]));
      
      dispatch(
        addToast({
          title: i18n.t("VehicleModel.ScanRequest"),
          type: "success",
          content: <Trans
            i18nKey={'VehicleModel.ScanRequested'}
            components={{ primary: <Text type="success" strong />, secondary: <Text strong /> }}
          />,
        })
      );
    } catch (error) {
      dispatch(
        addToast({
          title: i18n.t("VehicleModel.ScanRequest"),
          type: "error",
          content: i18n.t("Common.ErrorOccurred"),
        })
      );
    }
  };

  /**
   * Cancel/unvote for a scan request
   * @param {number} recordId - Vehicle model ID
   */
  const handleUnvote = async (recordId) => {
    try {
      await unVoteForScanRequest({ id: recordId });
      setVotes(prev => {
        const newVotes = new Set(prev);
        newVotes.delete(recordId);
        return newVotes;
      });
      
      dispatch(
        addToast({
          title: i18n.t("VehicleModel.ScanRequest"),
          type: "success",
          content: <Trans
            i18nKey={'VehicleModel.RequestCancelled'}
            components={{ primary: <Text type="success" strong />, secondary: <Text strong /> }}
          />,
        })
      );
    } catch (error) {
      dispatch(
        addToast({
          title: i18n.t("VehicleModel.ScanRequest"),
          type: "error",
          content: i18n.t("Common.ErrorOccurred"),
        })
      );
    }
  };

  return {
    // Methods
    getScanRequestState,
    handleVote,
    handleUnvote
  };
};

export default useScanRequestState;